/**
 * RunPod Service (via Cloudflare Worker Proxy)
 * Manages on-demand upscaler pod lifecycle through API gateway
 *
 * ARCHITECTURE:
 * - Token authentication handled by useTokenService & bearer-token-interceptor
 * - All worker API calls use upscalerApi (which auto-adds token via interceptor)
 * - All pod operations proxied through worker (consistent auth, security)
 */

import { ref, Ref } from 'vue';
import { PodStatus } from '@/model/pod-status';
import { useApi } from '@/api/api';
import { useInterval } from '@/utils/interval';
import { getHeaders } from '@/api/bearer-token.interceptor';

export class RunpodService {
  private readonly podName: string;
  podStatus: Ref<PodStatus | null>;

  constructor(podName: string) {
    this.podName = podName;
    this.podStatus = ref<PodStatus | null>(null);
    console.log(`[RunpodService] Initialized for pod: ${podName}`);
  }

  /**
   * Validate authentication token with worker
   */
  async validateToken(token: string): Promise<boolean> {
    if (!token || token.trim().length === 0) {
      console.warn('[RunpodService] No authentication token');
      return false;
    }

    try {
      const { authenticationApi } = useApi();
      const response = await authenticationApi.post('/login', {}, { headers: getHeaders(token) });

      console.log('[RunpodService] Token validation response:', response.status, response.data);
      if (response.status === 200 && response.data?.valid) {
        console.log('[RunpodService] ✅ Authentication token is valid');
        return true;
      }

      console.warn('[RunpodService] ❌ Authentication token is invalid');
      return false;
    } catch (error) {
      console.error('[RunpodService] Error validating token:', error);
      return false;
    }
  }

  /**
   * Get current pod info
   * @returns PodStatus or null if pod doesn't exist/not running
   */
  async getPodInfo(): Promise<PodStatus | null> {
    try {
      const { upscalerApi } = useApi();
      const response = await upscalerApi.get(`/pods/${this.podName}`, {
        validateStatus: () => true, // Don't throw on any status code
      });

      if (response.status === 404) {
        console.log('[RunpodService] 📭 No pod found');
        return null;
      }

      if (!response.status.toString().startsWith('2')) {
        console.error(`[RunpodService] Error fetching pod info: HTTP ${response.status}`);
        return null;
      }

      const data = response.data;

      // Handle not_running status
      if (data.status !== 'RUNNING' || !data.pod) {
        console.log('[RunpodService] 📭 Pod not running');
        return null;
      }

      // Parse RunPod response format: { pod: {...}, status: "RUNNING" }
      const pod = data.pod;

      // Convert worker response to PodStatus
      const status = new PodStatus(pod.id, this.podName, data.status, pod.gpuCount, pod.costPerHr);

      console.log('[RunpodService] ✅ Pod info:', { id: pod.id, status: data.status });
      this.podStatus.value = status; // Cache pod status
      return status;
    } catch (error) {
      console.error('[RunpodService] Error getting pod info:', error);
      return null;
    }
  }

  /**
   * Start a pod (single request, no retries)
   * @throws Error if start fails (503 or other errors)
   */
  async startPod(): Promise<void> {
    try {
      console.log(`[RunpodService] 🚀 Sending pod start request`);

      const { upscalerApi } = useApi();
      const response = await upscalerApi.post(
        `/pods/${this.podName}`,
        {},
        {
          validateStatus: () => true, // Don't throw on any status code
        },
      );

      if (response.status === 503) {
        console.warn('[RunpodService] ⚠️ No resources available (503)');
        const error: any = new Error('No resources available');
        error.statusCode = 503;
        throw error;
      }

      if (!response.status.toString().startsWith('2')) {
        const errorMsg = response.data?.error || `HTTP ${response.status}`;
        console.error('[RunpodService] Pod start error:', response.status, errorMsg);
        throw new Error(errorMsg);
      }

      console.log('[RunpodService] ✅ Pod start request accepted');
    } catch (error) {
      console.error('[RunpodService] ❌ Failed to start pod:', error);
      throw error;
    }
  }

  async startAndWaitForPod() {
    console.log('---------------------');
    console.log('[RunpodService] Step: Checking pod status...');
    const podInfo = await this.getPodInfo();
    console.log('Step result - pod info:', podInfo);

    if (!podInfo || podInfo.status !== 'RUNNING') {
      console.log('[RunpodService] Step: Pod not running, attempting to start...');
      await this.startPod();
      console.log('[RunpodService] Step: Pod not running, startPod called', this.podStatus.value);
    } else {
      console.log('[RunpodService] Step: Pod is running, sending heartbeat to confirm readiness...');
      const result = await this.sendHeartbeat();
      console.log('Heartbeat result:', result);
      if (result) return true; // Pod is ready
    }

    return false;
  }

  async ensurePodReady(): Promise<PodStatus> {
    const interval = useInterval(this.startAndWaitForPod.bind(this), 10000, 30); // Check every 10 seconds, max 30 retries
    await interval.start(true);
    return this.podStatus.value as PodStatus;
  }

  /**
   * Send heartbeat to pod to verify it's alive and ready to receive requests
   * Retries with exponential backoff if pod doesn't respond
   * @param maxWaitSeconds Maximum time to wait for heartbeat response
   * @throws Error if heartbeat fails or pod not available
   */
  async sendHeartbeat(maxWaitSeconds: number = 60): Promise<boolean> {
    if (!this.podStatus.value) {
      throw new Error('Pod status not available - cannot send heartbeat');
    }

    try {
      console.log(`[RunpodService] 💓 Sending heartbeat to pod...`);
      const { heartbeatApi } = useApi();
      const response = await heartbeatApi.post(`/pods/${this.podName}/heartbeat`, {});

      if (response.status === 200 && response.data?.status === 'heartbeat_received') {
        console.log('[RunpodService] ✅ Heartbeat received - pod is ready');
        return true; // Success!
      }

      console.warn(`[RunpodService] ⚠️ Heartbeat failed: HTTP ${response.status}`);
    } catch (error) {
      console.error('[RunpodService] ❌ Heartbeat error:', error);
    }

    return false;
  }

  /**
   * Stop the pod
   */
  async stopPod(): Promise<void> {
    try {
      console.log(`[RunpodService] ⏹️ Stopping pod: ${this.podName}`);

      const { upscalerApi } = useApi();
      const response = await upscalerApi.delete(`/pods/${this.podName}`, {
        validateStatus: () => true,
      });

      if (!response.status.toString().startsWith('2')) {
        const errorMsg = response.data?.error || `HTTP ${response.status}`;
        throw new Error(errorMsg);
      }

      console.log(`[RunpodService] ✅ Pod stop command sent`);
    } catch (error) {
      console.error('[RunpodService] ❌ Failed to stop pod:', error);
      throw error;
    }
  }
}
